import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  X, FolderSimple, FilePdf, Image, File, FileText,
  ArrowsClockwise, MicrosoftOutlookLogo, Warning, MagnifyingGlass,
} from "@phosphor-icons/react";
import { getDdRoot, listFolder, downloadFileItem } from "../../api/microsoft";

const DD_FOLDER_NAME = "DD Project Planner";

// Supported extensions for drawing tools
const SUPPORTED_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".bmp", ".gif", ".tiff", ".tif", ".dwg", ".dxf"];

function getFileIcon(name) {
  const ext = name?.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return { Icon: FilePdf, color: "text-red-500" };
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "heic"].includes(ext))
    return { Icon: Image, color: "text-emerald-500" };
  if (["doc", "docx", "txt"].includes(ext)) return { Icon: FileText, color: "text-blue-500" };
  return { Icon: File, color: "text-slate-400" };
}

function isSupported(name) {
  const ext = "." + (name?.split(".").pop()?.toLowerCase() || "");
  return SUPPORTED_EXTS.includes(ext);
}

function formatSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * OneDriveFilePicker
 *
 * A modal that lists files from the user's "DD Project Planner" folder
 * and calls onSelect(file) with a File object when the user picks one.
 *
 * Props:
 *   onSelect(file: File) — called with the downloaded File object
 *   onClose()            — called when the picker is dismissed
 */
export default function OneDriveFilePicker({ onSelect, onClose }) {
  const [msConnected, setMsConnected] = useState(null); // null = checking
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const root = await getDdRoot();
      const items = await listFolder(root.id);
      setFiles(items.filter((f) => !f.folder));
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load files from OneDrive.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    import("../../api/microsoft").then(({ getMsStatus }) => {
      getMsStatus().then(({ connected }) => {
        setMsConnected(connected);
        if (connected) loadFiles();
      }).catch(() => setMsConnected(false));
    });
  }, [loadFiles]);

  const handleSelect = async (item) => {
    if (downloadingId) return;
    if (!isSupported(item.name)) {
      toast.error(`"${item.name}" is not a supported file type.`);
      return;
    }
    setDownloadingId(item.id);
    try {
      const resp = await downloadFileItem(item.id);
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
      const file = new File([blob], item.name, { type: blob.type });
      onSelect(file);
      onClose();
    } catch (e) {
      toast.error(`Failed to load file: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const filteredFiles = files.filter((f) =>
    f.name?.toLowerCase().includes(search.toLowerCase())
  );
  const supportedFiles = filteredFiles.filter((f) => isSupported(f.name));
  const unsupportedFiles = filteredFiles.filter((f) => !isSupported(f.name));
  const displayFiles = [...supportedFiles, ...unsupportedFiles];

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="onedrive-picker-backdrop"
    >
      <div
        className="bg-white w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]"
        data-testid="onedrive-picker-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            {/* OneDrive logo */}
            <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" fill="none">
              <path
                d="M9.5 7.5C10.83 5.3 13.28 4 16 4C19.87 4 23 7.13 23 11C23 11.34 22.97 11.68 22.93 12C23.56 12.35 24 13.03 24 13.83C24 14.92 23.09 15.83 22 15.83H8.5C6.57 15.83 5 14.26 5 12.33C5 10.51 6.38 9.02 8.17 8.86C8.31 8.23 8.85 7.73 9.5 7.5Z"
                fill="#0078D4"
                opacity="0.6"
              />
              <path
                d="M3.5 11C1.57 11 0 12.57 0 14.5C0 16.43 1.57 18 3.5 18H19.5C20.88 18 22 16.88 22 15.5C22 14.12 20.88 13 19.5 13H19C18.45 10.2 16 8 13 8C10.73 8 8.73 9.22 7.69 11.06C6.7 10.42 5.15 10.5 4.5 11H3.5Z"
                fill="#0078D4"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-slate-800">Select from OneDrive</p>
              <p className="text-[11px] text-slate-400 flex items-center gap-1">
                <FolderSimple size={11} className="text-[#0078D4]" />
                {DD_FOLDER_NAME}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid="picker-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {msConnected === null ? (
            /* Checking */
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8" data-testid="picker-loading">
              <ArrowsClockwise size={24} className="text-[#0078D4] animate-spin" />
              <p className="text-sm text-slate-500">Checking connection…</p>
            </div>
          ) : !msConnected ? (
            /* Not connected */
            <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center" data-testid="picker-not-connected">
              <div className="w-12 h-12 bg-[#0078D4]/10 flex items-center justify-center rounded-sm">
                <MicrosoftOutlookLogo size={22} className="text-[#0078D4]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800 mb-1">Server access not granted</p>
                <p className="text-xs text-slate-400 max-w-xs">
                  Grant server access from AI Automations to browse the "{DD_FOLDER_NAME}" folder.
                </p>
              </div>
            </div>
          ) : loading ? (
            /* Loading */
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8" data-testid="picker-loading">
              <ArrowsClockwise size={24} className="text-[#0078D4] animate-spin" />
              <p className="text-sm text-slate-500">Loading files from OneDrive…</p>
            </div>
          ) : error ? (
            /* Error */
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center" data-testid="picker-error">
              <Warning size={22} className="text-red-400" />
              <p className="text-sm text-slate-600">{error}</p>
              <button
                onClick={loadFiles}
                className="text-xs text-[#0078D4] hover:underline"
              >
                Retry
              </button>
            </div>
          ) : (
            /* File list */
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Search */}
              {files.length > 4 && (
                <div className="px-3 pt-3 pb-2 flex-shrink-0">
                  <div className="relative">
                    <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Filter files…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0078D4]/50"
                      data-testid="picker-search"
                    />
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto" data-testid="picker-file-list">
                {displayFiles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid="picker-empty">
                    <FolderSimple size={28} className="text-slate-300" />
                    <p className="text-sm text-slate-500">
                      {search ? `No files match "${search}"` : `No files in "${DD_FOLDER_NAME}"`}
                    </p>
                    {!search && (
                      <p className="text-xs text-slate-400">
                        Upload files to your OneDrive folder first.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {displayFiles.map((item) => {
                      const { Icon, color } = getFileIcon(item.name);
                      const supported = isSupported(item.name);
                      const isDownloading = downloadingId === item.id;

                      return (
                        <button
                          key={item.id}
                          onClick={() => supported && handleSelect(item)}
                          disabled={!!downloadingId || !supported}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                            ${supported && !downloadingId
                              ? "hover:bg-[#0078D4]/5 cursor-pointer"
                              : "cursor-not-allowed opacity-40"
                            }
                            ${isDownloading ? "bg-[#0078D4]/5" : ""}
                          `}
                          data-testid={`picker-file-${item.name}`}
                        >
                          {isDownloading ? (
                            <ArrowsClockwise size={16} className="text-[#0078D4] animate-spin flex-shrink-0" />
                          ) : (
                            <Icon size={16} className={`${color} flex-shrink-0`} />
                          )}
                          <span className="flex-1 text-xs font-medium text-slate-700 truncate">
                            {item.name}
                          </span>
                          <span className="text-[10px] text-slate-400 flex-shrink-0">
                            {isDownloading ? "Loading…" : formatSize(item.size)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {msConnected && !loading && !error && (
          <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-slate-50">
            <span className="text-[10px] text-slate-400">
              {supportedFiles.length} supported file{supportedFiles.length !== 1 ? "s" : ""}
            </span>
            <button
              onClick={loadFiles}
              className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
              data-testid="picker-refresh-btn"
            >
              <ArrowsClockwise size={10} />
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
